import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import Swal from 'sweetalert2';
import { useAuth } from './context/AuthContext';
import LockOverlay from './components/LockOverlay';
import UrunModifierSheet from './components/UrunModifierSheet';
import { fm } from './utils';
import './GarsonPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const groupModifiers = (list) => {
  const groups = {};
  (list || []).forEach(m => {
    const gName = m.modifierGroupAdi || 'Diğer';
    if (!groups[gName]) groups[gName] = [];
    const qty = m.qty || 1;
    groups[gName].push({ adi: m.modifierItemAdi, adet: qty });
  });
  return groups;
};

const normalizeStr = (s) => (s || '')
  .toLowerCase()
  .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
  .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c');

const MasaCard = ({ masa, ozet, onClick }) => {
  const durum = masa.durum === 'Boş' ? 'bos' : 'aktif';
  const renkMap = {
    bos: { bg: 'bg-gray-50 dark:bg-surface-800', border: 'border-gray-200 dark:border-surface-700', badge: 'bg-gray-200 dark:bg-surface-600 text-gray-500 dark:text-surface-400', dot: 'bg-gray-300' },
    aktif: { bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200 dark:border-amber-700', badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  };
  const renk = renkMap[durum];
  return (
    <button
      onClick={onClick}
      className={`relative rounded-2xl border-2 p-4 text-left transition-all duration-200 active:scale-[0.97] touch-manipulation ${renk.bg} ${renk.border} hover:shadow-md`}
    >
      {durum === 'bos' ? (
        <div className="flex flex-col items-center justify-center py-4 gap-2">
          <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-surface-700 flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-gray-500 dark:text-surface-400">{masa.masaNumarasi}</span>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-lg font-bold text-surface-800 dark:text-white">{masa.masaNumarasi}</span>
            <span className={`w-2.5 h-2.5 rounded-full ${renk.dot}`} />
          </div>
          <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold ${renk.badge}`}>Aktif</span>
        </>
      )}
    </button>
  );
};

const UrunCard = ({ urun, adet, onEkle, onAdetGuncelle, onLongPress, onNotGuncelle }) => {
  const modifierliUrun = urun.hasModifiers;
  const [animating, setAnimating] = useState(false);
  const [notMetin, setNotMetin] = useState('');
  const [notPopup, setNotPopup] = useState(false);
  const touchTimer = useRef(null);

  const handleTouchStart = () => { touchTimer.current = setTimeout(() => onLongPress && onLongPress(urun), 500); };
  const handleTouchMove = () => { clearTimeout(touchTimer.current); };
  const handleTouchEnd = () => { clearTimeout(touchTimer.current); };
  const handleContextMenu = (e) => { e.preventDefault(); onLongPress && onLongPress(urun); };

  const handleEkle = () => { setAnimating(true); onEkle({ ...urun, not: notMetin }); setTimeout(() => setAnimating(false), 200); };
  const handleNotKaydet = () => { if (adet > 0 && onNotGuncelle) onNotGuncelle(urun.urunID, notMetin); };

  const notPopupKaydet = () => {
    handleNotKaydet();
    if (adet === 0) handleEkle();
    setNotPopup(false);
  };

  return (
    <>
      <div
        className={`bg-white dark:bg-surface-800 rounded-xl border border-gray-100 dark:border-surface-700 p-3 transition-all duration-150 relative ${animating ? 'scale-95' : ''} ${modifierliUrun && adet > 0 ? 'cursor-pointer' : ''}`}
        onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onContextMenu={handleContextMenu}
        onClick={() => { if (modifierliUrun && adet > 0) handleEkle(); }}
      >
        <button onClick={() => setNotPopup(true)}
          className={`absolute top-2 right-2 w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${notMetin ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-500' : 'bg-gray-100 dark:bg-surface-700 text-gray-400 hover:text-amber-500'}`}
          title="Not ekle">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <div className="mb-2">
          <h4 className="text-xs font-semibold text-surface-800 dark:text-white leading-tight line-clamp-2">{urun.urunAdi}</h4>
          <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-1">{fm(urun.fiyat)}</p>
        </div>
        {adet > 0 ? (
          <div className="flex items-center justify-between bg-gray-50 dark:bg-surface-700/50 rounded-lg p-0.5 animate-fade-in">
            <button onClick={() => onAdetGuncelle(urun.urunID, adet - 1)} className="w-8 h-8 rounded-lg bg-white dark:bg-surface-700 border border-gray-200 dark:border-surface-600 flex items-center justify-center text-gray-500 dark:text-surface-400 active:scale-90 touch-manipulation text-lg font-bold">−</button>
            <span className="w-6 text-center text-sm font-bold text-surface-800 dark:text-white tabular-nums">{adet}</span>
            <button onClick={() => modifierliUrun ? handleEkle() : onAdetGuncelle(urun.urunID, adet + 1)} className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white active:scale-90 touch-manipulation text-lg font-bold">+</button>
          </div>
        ) : (
          <button onClick={handleEkle} className="w-full h-9 rounded-lg bg-gray-50 dark:bg-surface-700/50 border border-gray-100 dark:border-surface-600 flex items-center justify-center text-gray-400 dark:text-surface-500 hover:bg-gray-100 dark:hover:bg-surface-600 active:bg-emerald-50 active:text-emerald-500 transition-colors touch-manipulation">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {notPopup && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setNotPopup(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full bg-white dark:bg-surface-800 rounded-t-2xl shadow-2xl animate-slide-up p-5 pb-8 max-h-[85vh] overflow-y-auto keyboard-aware-sheet" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-surface-600 mx-auto mb-4" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-surface-800 dark:text-white text-base">{urun.urunAdi}</h3>
              <button onClick={() => setNotPopup(false)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-surface-700 text-gray-400 touch-manipulation">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mb-4">{fm(urun.fiyat)}</p>
            <textarea value={notMetin} onChange={e => setNotMetin(e.target.value)}
              placeholder="Ürün notu (ör: az şekerli...)"
              rows={5}
              onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400)}
              className="w-full text-sm bg-gray-50 dark:bg-surface-700/50 border border-gray-200 dark:border-surface-600 rounded-xl p-3 text-surface-700 dark:text-surface-300 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              autoFocus
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => setNotPopup(false)} className="flex-1 py-3 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-surface-700 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-colors touch-manipulation">İptal</button>
              <button onClick={() => { setNotMetin(''); if (adet > 0 && onNotGuncelle) onNotGuncelle(urun.urunID, ''); setNotPopup(false); }} className="flex-1 py-3 text-xs font-semibold text-red-500 bg-red-50 dark:bg-red-900/10 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors touch-manipulation">Temizle</button>
              <button onClick={notPopupKaydet} className="flex-[2] py-3 text-xs font-semibold text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.97] touch-manipulation">
                {adet > 0 ? 'Notu Kaydet' : 'Sepete Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SepetItem = ({ item, onAdetGuncelle, onNotGuncelle }) => {
  const [expanded, setExpanded] = useState(false);
  const [notAcik, setNotAcik] = useState(false);
  const [notMetin, setNotMetin] = useState(item.not || '');
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);
  const handleTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { const dx = e.touches[0].clientX - startX.current; if (dx < -60) setSwiping(true); else setSwiping(false); };
  const handleTouchEnd = () => { if (swiping) { onAdetGuncelle(item.urunID, 0); setSwiping(false); } };

  const groupedModifiers = useMemo(() => {
    if (!item.modifierDetaylari || item.modifierDetaylari.length === 0) return null;
    const groups = {};
    item.modifierDetaylari.forEach(m => {
      if (!groups[m.modifierGroupAdi]) groups[m.modifierGroupAdi] = [];
      groups[m.modifierGroupAdi].push(m);
    });
    return groups;
  }, [item.modifierDetaylari]);

  return (
    <div className={`bg-white dark:bg-surface-700/30 rounded-xl border border-gray-100 dark:border-surface-700 transition-all ${swiping ? 'opacity-50 scale-95' : ''}`}
      onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-surface-800 dark:text-surface-200 truncate">{item.urunAdi}</p>
          {groupedModifiers && (
            <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 mt-0.5 touch-manipulation">
              <span className="text-[9px] text-surface-400 truncate">
                {Object.entries(groupedModifiers).map(([gAdi, items]) => {
                  const summary = items.map(m => { const q = m.qty || 1; return q > 1 ? `${q}x ${m.modifierItemAdi}` : m.modifierItemAdi; }).join(', ');
                  return `${gAdi}: ${summary}`;
                }).join(' | ')}
              </span>
              <svg className={`w-3 h-3 text-surface-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-[10px] text-surface-400">{fm(item.fiyat * item.adet)}</p>
            {item.not && <span className="text-[9px] text-amber-500 truncate max-w-[120px]">📝 {item.not}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => { setNotAcik(!notAcik); if (!notAcik) setNotMetin(item.not || ''); }}
            className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors touch-manipulation ${item.not ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-500' : 'bg-gray-100 dark:bg-surface-700 text-gray-400 hover:text-amber-500'}`}
            title="Not ekle">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
          </button>
          <button onClick={() => onAdetGuncelle(item.urunID, item.adet - 1)} className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-surface-700 flex items-center justify-center text-gray-500 dark:text-surface-400 active:scale-90 touch-manipulation text-xs font-bold">−</button>
          <span className="w-5 text-center text-xs font-bold text-surface-800 dark:text-surface-200 tabular-nums">{item.adet}</span>
          <button onClick={() => onAdetGuncelle(item.urunID, item.adet + 1)} className="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center text-white active:scale-90 touch-manipulation text-xs font-bold">+</button>
        </div>
        <button onClick={() => onAdetGuncelle(item.urunID, 0)} className="w-6 h-6 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors touch-manipulation">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {notAcik && (
        <div className="px-3 pb-2.5 animate-fade-in">
          <textarea value={notMetin} onChange={e => setNotMetin(e.target.value)}
            placeholder="Ürün notu (ör: az şekerli...)"
            rows={2}
            className="w-full text-[11px] bg-gray-50 dark:bg-surface-700/50 border border-gray-200 dark:border-surface-600 rounded-lg p-2 text-surface-700 dark:text-surface-300 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            autoFocus
          />
          <div className="flex justify-end gap-1 mt-1">
            <button onClick={() => { setNotMetin(''); onNotGuncelle(item.urunID, ''); setNotAcik(false); }} className="px-2 py-1 text-[9px] font-medium text-gray-500 bg-gray-100 dark:bg-surface-700 rounded-lg hover:bg-gray-200 dark:hover:bg-surface-600 transition-colors">Temizle</button>
            <button onClick={() => { onNotGuncelle(item.urunID, notMetin); setNotAcik(false); }} className="px-2 py-1 text-[9px] font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors">Tamam</button>
          </div>
        </div>
      )}
      {expanded && groupedModifiers && (
        <div className="px-3 pb-2.5 space-y-1 animate-fade-in">
          {Object.entries(groupedModifiers).map(([gAdi, items]) => (
            <div key={gAdi} className="flex items-start gap-2 text-[10px]">
              <span className="font-medium text-surface-500 dark:text-surface-400 w-16 flex-shrink-0">{gAdi}:</span>
              <span className="text-surface-600 dark:text-surface-300">
                {items.map(m => { const q = m.qty || 1; return q > 1 ? `${q}x ${m.modifierItemAdi}` : m.modifierItemAdi; }).join(', ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UrunDetaySheet = ({ urun, onClose, onNotGuncelle }) => {
  const [not, setNot] = useState('');
  if (!urun) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full bg-white dark:bg-surface-800 rounded-t-2xl shadow-2xl animate-slide-up p-5 pb-8 max-h-[85vh] overflow-y-auto keyboard-aware-sheet" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-surface-600 mx-auto mb-4" />
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-surface-800 dark:text-white text-base">{urun.urunAdi}</h3>
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mt-0.5">{fm(urun.fiyat)}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-surface-700 text-gray-400 touch-manipulation">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <textarea value={not} onChange={e => setNot(e.target.value)}
          placeholder="Ürün notu (ör: az şekerli...)"
          rows={3}
          onFocus={e => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400)}
          className="w-full text-sm bg-gray-50 dark:bg-surface-700/50 border border-gray-200 dark:border-surface-600 rounded-xl p-3 text-surface-700 dark:text-surface-300 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          autoFocus
        />
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="flex-1 py-3 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-surface-700 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-colors touch-manipulation">İptal</button>
          <button onClick={() => { onNotGuncelle(urun.urunID, not); onClose(); }} className="flex-1 py-3 text-xs font-semibold text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 transition-colors touch-manipulation">Not Ekle & Sepete Ekle</button>
        </div>
      </div>
    </div>
  );
};

const KategoriItem = ({ kat, aktif, urunSayisi, onClick }) => (
  <button onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-all touch-manipulation ${
      aktif ? 'bg-emerald-50 dark:bg-emerald-900/10 border-l-2 border-emerald-500 -ml-px' : 'hover:bg-gray-50 dark:hover:bg-surface-700/30 border-l-2 border-transparent -ml-px'
    }`}>
    <span className={`flex-1 text-xs font-medium ${aktif ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-surface-600 dark:text-surface-400'}`}>{kat.kategoriAdi}</span>
    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${aktif ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-gray-100 dark:bg-surface-700 text-gray-400 dark:text-surface-500'}`}>{urunSayisi}</span>
  </button>
);

const MasaTasiSheet = ({ kaynakMasa, masalar, onClose, onTasi }) => {
  const [hedefId, setHedefId] = useState(null);
  const [birlestir, setBirlestir] = useState(false);
  const hedefMasa = masalar.find(m => m.masaID === hedefId);
  const doluMu = hedefMasa?.durum === 'Dolu';

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full bg-white dark:bg-surface-800 rounded-t-2xl shadow-2xl animate-slide-up max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-surface-600 mx-auto mt-3 mb-2" />
        <div className="px-5 pb-8">
          <h3 className="font-bold text-surface-800 dark:text-white text-base mb-1">Masa Taşı</h3>
          <p className="text-xs text-surface-400 mb-4">Kaynak: {kaynakMasa.masaNumarasi} → Hedef masa seçin</p>

          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mb-4">
            {masalar.filter(m => m.masaID !== kaynakMasa.masaID).map(m => {
              const aktif = hedefId === m.masaID;
              const bos = m.durum === 'Boş';
              return (
                <button key={m.masaID} onClick={() => { setHedefId(m.masaID); setBirlestir(false); }}
                  className={`rounded-xl border-2 p-3 text-center transition-all touch-manipulation ${
                    aktif ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10 shadow-md' :
                    bos ? 'border-gray-200 dark:border-surface-700 bg-gray-50 dark:bg-surface-800 hover:border-emerald-300' :
                    'border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 hover:border-amber-400'
                  }`}>
                  <span className={`text-sm font-bold ${aktif ? 'text-emerald-600' : bos ? 'text-surface-600 dark:text-surface-300' : 'text-amber-600 dark:text-amber-400'}`}>{m.masaNumarasi}</span>
                  <span className={`block text-[9px] font-medium mt-0.5 ${bos ? 'text-gray-400' : 'text-amber-500'}`}>{bos ? 'Boş' : 'Dolu'}</span>
                </button>
              );
            })}
          </div>

          {hedefId && doluMu && (
            <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-3 mb-4">
              <label className="flex items-center gap-2.5 cursor-pointer touch-manipulation">
                <input type="checkbox" checked={birlestir} onChange={e => setBirlestir(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Siparişleri birleştir</span>
              </label>
              <p className="text-[10px] text-amber-500 dark:text-amber-500 mt-1.5 ml-6">İşaretlenirse her iki masanın siparişleri tek çatı altında birleşir.</p>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 text-xs font-semibold bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-surface-400 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-all touch-manipulation">İptal</button>
            <button onClick={() => hedefId && onTasi(hedefId, birlestir)} disabled={!hedefId}
              className="flex-1 py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation">
              {hedefId ? (birlestir ? 'Birleştir & Taşı' : 'Taşı') : 'Hedef Seçin'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UrunTasiSheet = ({ urun, kaynakSiparis, masalar, onClose, onTasi }) => {
  const [adet, setAdet] = useState(1);
  const [hedefId, setHedefId] = useState(null);

  const mevcutAdet = urun?.adet || 1;

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full bg-white dark:bg-surface-800 rounded-t-2xl shadow-2xl animate-slide-up max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-surface-600 mx-auto mt-3 mb-2" />
        <div className="px-5 pb-8">
          <h3 className="font-bold text-surface-800 dark:text-white text-base mb-1">Ürün Taşı</h3>
          <p className="text-xs text-surface-400 mb-4">{urun?.urunAdi} → Hedef masa seçin</p>

          <div className="bg-gray-50 dark:bg-surface-700/30 rounded-xl p-3 mb-4">
            <label className="block text-[11px] font-medium text-surface-400 mb-2">Taşınacak Adet</label>
            <div className="flex items-center gap-3">
              <button onClick={() => setAdet(Math.max(1, adet - 1))} disabled={adet <= 1}
                className="w-10 h-10 rounded-xl bg-white dark:bg-surface-700 border border-gray-200 dark:border-surface-600 flex items-center justify-center text-gray-500 dark:text-surface-400 active:scale-90 disabled:opacity-30 touch-manipulation text-lg font-bold">−</button>
              <span className="w-10 text-center text-lg font-bold text-surface-800 dark:text-white tabular-nums">{adet}</span>
              <button onClick={() => setAdet(Math.min(mevcutAdet, adet + 1))} disabled={adet >= mevcutAdet}
                className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white active:scale-90 disabled:opacity-30 touch-manipulation text-lg font-bold">+</button>
              <span className="text-[11px] text-surface-400 ml-1">/ {mevcutAdet}</span>
            </div>
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mb-4">
            {masalar.filter(m => m.masaID !== kaynakSiparis?.masaID).map(m => {
              const aktif = hedefId === m.masaID;
              const bos = m.durum === 'Boş';
              return (
                <button key={m.masaID} onClick={() => setHedefId(m.masaID)}
                  className={`rounded-xl border-2 p-3 text-center transition-all touch-manipulation ${
                    aktif ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10 shadow-md' :
                    bos ? 'border-gray-200 dark:border-surface-700 bg-gray-50 dark:bg-surface-800 hover:border-emerald-300' :
                    'border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 hover:border-amber-400'
                  }`}>
                  <span className={`text-sm font-bold ${aktif ? 'text-emerald-600' : bos ? 'text-surface-600 dark:text-surface-300' : 'text-amber-600 dark:text-amber-400'}`}>{m.masaNumarasi}</span>
                  <span className={`block text-[9px] font-medium mt-0.5 ${bos ? 'text-gray-400' : 'text-amber-500'}`}>{bos ? 'Boş' : 'Dolu'}</span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 text-xs font-semibold bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-surface-400 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-all touch-manipulation">İptal</button>
            <button onClick={() => hedefId && onTasi(hedefId, adet)} disabled={!hedefId}
              className="flex-1 py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation">
              {hedefId ? `${adet} adet → Masa ${masalar.find(m => m.masaID === hedefId)?.masaNumarasi}` : 'Hedef Seçin'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MasaDetayPopup = ({ masa, mevcutSiparis, masalar, onClose, onSipariseGit, onMasaTasi, onUrunTasiBaslat }) => {
  const [sayfa, setSayfa] = useState('menu');
  const [urunTasiUrun, setUrunTasiUrun] = useState(null);

  if (sayfa === 'masa-tasi') {
    return (
      <MasaTasiSheet
        kaynakMasa={masa}
        masalar={masalar}
        onClose={() => setSayfa('menu')}
        onTasi={(hedefId, birlestir) => {
          onMasaTasi(hedefId, birlestir);
          onClose();
        }}
      />
    );
  }

  if (sayfa === 'urun-tasi' && urunTasiUrun) {
    return (
      <UrunTasiSheet
        urun={urunTasiUrun}
        kaynakSiparis={{ masaID: masa.masaID, masaNumarasi: masa.masaNumarasi }}
        masalar={masalar}
        onClose={() => setSayfa('menu')}
        onTasi={(hedefId, adet) => {
          onUrunTasiBaslat(urunTasiUrun, hedefId, adet);
          onClose();
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full bg-white dark:bg-surface-800 rounded-t-2xl shadow-2xl animate-slide-up max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-surface-600 mx-auto mt-3 mb-2" />
        <div className="px-5 pb-8">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-surface-800 dark:text-white text-lg">Masa {masa.masaNumarasi}</h3>
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">Aktif</span>
          </div>
          <p className="text-xs text-surface-400 mb-4">{mevcutSiparis?.siparisDetaylari?.length || 0} ürün • {fm(mevcutSiparis?.toplamTutar || 0)}</p>

          {mevcutSiparis?.siparisDetaylari && (
            <div className="space-y-1.5 mb-4 max-h-48 overflow-y-auto custom-scrollbar">
              {mevcutSiparis.siparisDetaylari.map((d, i) => (
                <div key={d.detayID || i}
                  className="bg-gray-50 dark:bg-surface-700/30 rounded-xl px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-surface-700/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-surface-700 dark:text-surface-300 truncate">{d.adet}x {d.urunAdi}</p>
                      {d.modifiers && d.modifiers.length > 0 && (() => {
                        const groups = groupModifiers(d.modifiers);
                        return (
                          <div className="text-[9px] text-surface-400 mt-0.5 leading-tight space-y-0.5">
                            {Object.entries(groups).map(([gAdi, items]) => (
                              <div key={gAdi} className="flex items-start gap-1">
                                <span className="font-medium text-surface-500 flex-shrink-0">{gAdi}:</span>
                                <span className="truncate">{items.map(i => i.adet > 1 ? `${i.adet}x ${i.adi}` : i.adi).join(', ')}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      {d.notlar && <p className="text-[9px] text-amber-500 truncate">📝 {d.notlar}</p>}
                    </div>
                    <span className="text-[10px] font-medium text-surface-400 flex-shrink-0 ml-2">{fm(d.toplamFiyat || d.birimFiyat * d.adet)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <button onClick={() => { onSipariseGit(); onClose(); }}
              className="w-full py-3 text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.97] touch-manipulation">
              Sipariş Detayı
            </button>
            <button onClick={() => setSayfa('masa-tasi')}
              className="w-full py-3 text-sm font-semibold bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all active:scale-[0.97] touch-manipulation">
              Masa Taşı
            </button>
            <button onClick={onClose}
              className="w-full py-3 text-sm font-semibold bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-surface-400 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-all active:scale-[0.97] touch-manipulation">
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SiparisGosterPopup = ({ mevcutSiparis, sepet, onClose, onAdetGuncelle, onNotGuncelle }) => {
  const getModifiers = (d) => {
    if (d.modifiers) return d.modifiers;
    if (d.modifierItems) try { return JSON.parse(d.modifierItems); } catch { return undefined; }
    return undefined;
  };

  const [notDuzenlenen, setNotDuzenlenen] = useState(null);
  const [notMetin, setNotMetin] = useState('');

  const tumUrunler = [
    ...(mevcutSiparis?.siparisDetaylari || []).map(d => ({ ...d, modifiers: getModifiers(d), kaynak: 'mevcut' })),
    ...sepet.map(i => ({ detayID: `yeni-${i.urunID}-${Date.now()}`, urunID: i.urunID, urunAdi: i.urunAdi, adet: i.adet, birimFiyat: i.fiyat, toplamFiyat: i.fiyat * i.adet, modifiers: i.modifierDetaylari || [], not: i.not, kaynak: 'yeni' }))
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white dark:bg-surface-800 rounded-2xl shadow-2xl animate-scale-up max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-surface-700 sticky top-0 bg-white dark:bg-surface-800 z-10">
          <div>
            <h3 className="font-bold text-surface-800 dark:text-white text-base">Siparişler</h3>
            <p className="text-[11px] text-surface-400">{tumUrunler.length} ürün</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-surface-700 text-gray-400 touch-manipulation">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-4">
          {tumUrunler.length === 0 ? (
            <div className="text-center py-8 text-surface-400 text-sm">Henüz sipariş yok</div>
          ) : (
            <div className="space-y-2.5">
              {tumUrunler.map((d, idx) => {
                const grouped = d.modifiers ? groupModifiers(d.modifiers) : null;
                const yeniMi = d.kaynak === 'yeni';
                return (
                  <div key={d.detayID || idx} className="bg-gray-50 dark:bg-surface-700/30 rounded-xl px-3.5 py-3 relative">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {yeniMi ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => onAdetGuncelle(d.urunID, d.adet - 1)} className="w-6 h-6 rounded-lg bg-white dark:bg-surface-700 border border-gray-200 dark:border-surface-600 flex items-center justify-center text-gray-500 active:scale-90 text-xs font-bold">−</button>
                              <span className="w-5 text-center text-sm font-bold text-surface-800 dark:text-white tabular-nums">{d.adet}</span>
                              <button onClick={() => onAdetGuncelle(d.urunID, d.adet + 1)} className="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center text-white active:scale-90 text-xs font-bold">+</button>
                            </div>
                          ) : (
                            <span className="font-bold text-surface-800 dark:text-surface-200 text-sm">{d.adet}x</span>
                          )}
                          <span className="font-semibold text-surface-700 dark:text-surface-300 text-sm truncate">{d.urunAdi || '?'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        <span className="text-[12px] font-semibold text-surface-500">{fm(d.toplamFiyat || d.birimFiyat * d.adet)}</span>
                        {yeniMi && (
                          <>
                            <button onClick={() => { setNotDuzenlenen(notDuzenlenen === d.urunID ? null : d.urunID); setNotMetin(d.not || ''); }}
                              className={`w-6 h-6 rounded-lg flex items-center justify-center ${d.not ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-500' : 'bg-gray-100 dark:bg-surface-700 text-gray-400 hover:text-amber-500'}`}
                              title="Not düzenle">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            </button>
                            <button onClick={() => onAdetGuncelle(d.urunID, 0)} className="w-6 h-6 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                              title="İptal et">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {notDuzenlenen === d.urunID && yeniMi && (
                      <div className="mt-2 animate-fade-in">
                        <textarea value={notMetin} onChange={e => setNotMetin(e.target.value)}
                          placeholder="Ürün notu (ör: az şekerli...)"
                          rows={2}
                          className="w-full text-[11px] bg-white dark:bg-surface-700 border border-gray-200 dark:border-surface-600 rounded-lg p-2 text-surface-700 dark:text-surface-300 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          autoFocus
                        />
                        <div className="flex justify-end gap-1 mt-1">
                          <button onClick={() => { setNotMetin(''); onNotGuncelle(d.urunID, ''); setNotDuzenlenen(null); }} className="px-2 py-1 text-[9px] font-medium text-gray-500 bg-gray-100 dark:bg-surface-700 rounded-lg hover:bg-gray-200 dark:hover:bg-surface-600 transition-colors">Temizle</button>
                          <button onClick={() => { onNotGuncelle(d.urunID, notMetin); setNotDuzenlenen(null); }} className="px-2 py-1 text-[9px] font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors">Tamam</button>
                        </div>
                      </div>
                    )}
                    {grouped && Object.keys(grouped).length > 0 && (
                      <div className="mt-1.5 space-y-0.5 pl-2 border-l-2 border-emerald-200 dark:border-emerald-700">
                        {Object.entries(grouped).map(([gAdi, items]) => (
                          <div key={gAdi} className="text-[11px] text-surface-500 dark:text-surface-400">
                            <span className="font-medium text-surface-400">{gAdi}:</span>{' '}
                            {items.map(i => i.adet > 1 ? `${i.adet}x ${i.adi}` : i.adi).join(', ')}
                          </div>
                        ))}
                      </div>
                    )}
                    {(d.not || d.notlar) && notDuzenlenen !== d.urunID && (
                      <p className="text-[10px] text-amber-500 mt-1">📝 {d.not || d.notlar}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const GarsonPanel = () => {
  const { token, kullanici, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [screen, setScreen] = useState('masalar');
  const [seciliMasa, setSeciliMasa] = useState(null);
  const [masalar, setMasalar] = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  const [tumUrunler, setTumUrunler] = useState([]);
  const [seciliKategoriId, setSeciliKategoriId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sepet, setSepet] = useState([]);
  const [mevcutSiparis, setMevcutSiparis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [longPressUrun, setLongPressUrun] = useState(null);
  const [kategoriPanelAcik, setKategoriPanelAcik] = useState(false);
  const [masaDetayMasa, setMasaDetayMasa] = useState(null);
  const [masaTasiAcik, setMasaTasiAcik] = useState(false);
  const [urunTasiDetay, setUrunTasiDetay] = useState(null);
  const [modifierUrun, setModifierUrun] = useState(null);
  const [siparisGosterAcik, setSiparisGosterAcik] = useState(false);
  const [siparisGonderiliyor, setSiparisGonderiliyor] = useState(false);
  const searchRef = useRef(null);
  const mevcutSiparisRef = useRef(null);
  const modalAcikRef = useRef(false);

  useEffect(() => { mevcutSiparisRef.current = mevcutSiparis; }, [mevcutSiparis]);
  useEffect(() => { modalAcikRef.current = !!(modifierUrun || longPressUrun || siparisGosterAcik || masaTasiAcik || masaDetayMasa); });

  useEffect(() => {
    const init = async () => { await Promise.all([fetchMasalar(), fetchKategoriler(), fetchTumUrunler()]); setLoading(false); };
    init();
    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/siparis')
      .configureLogging(LogLevel.Information).build();
    connection.on('SiparisGuncellendi', () => { fetchMasalar(); setRefreshTrigger(p => p + 1); });
    connection.on('YeniSiparisAlindi', () => { fetchMasalar(); setRefreshTrigger(p => p + 1); });
    connection.on('SiparisDurumuDegisti', () => { fetchMasalar(); setRefreshTrigger(p => p + 1); });
    connection.on('SiparisKapatildi', (siparisId) => {
      fetchMasalar();
      setRefreshTrigger(p => p + 1);
      if (mevcutSiparisRef.current?.siparisID === siparisId) {
        setSeciliMasa(null);
        setMevcutSiparis(null);
        setSepet([]);
        setScreen('masalar');
      }
    });
    connection.start().catch(e => console.error('SignalR Error:', e));

    const interval = setInterval(() => {
      if (modalAcikRef.current) return;
      fetchMasalar();
      fetchKategoriler();
      fetchTumUrunler();
      setRefreshTrigger(p => p + 1);
    }, 30000);

    return () => { connection.stop(); clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (seciliMasa && seciliMasa.durum === 'Dolu') fetchMasaAktifSiparis(seciliMasa.masaID);
    else setMevcutSiparis(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seciliMasa, refreshTrigger]);

  useEffect(() => { setSepet([]); setSearchTerm(''); }, [seciliMasa]);

  const fetchTumUrunler = async () => { try { const r = await fetch(`${API_URL}/urunler`); setTumUrunler(await r.json()); } catch (e) { console.error(e); } };
  const fetchMasaAktifSiparis = async (masaId) => {
    try {
      const r = await fetch(`${API_URL}/siparisler/masa/${masaId}`, { headers });
      const data = await r.json();
      const aktif = data.find(s => s.siparisDurumu !== 'Tamamlandı' && s.siparisDurumu !== 'İptal');
      if (aktif) { const dr = await fetch(`${API_URL}/siparisler/${aktif.siparisID}`, { headers }); const json = await dr.json(); console.log('MODIFIER DEBUG:', JSON.stringify(json.siparisDetaylari?.map(d => ({ u: d.urunAdi, m: d.modifiers })))); setMevcutSiparis(json); }
      else setMevcutSiparis(null);
    } catch (e) { console.error(e); }
  };
  const fetchKategoriler = async () => { try { const r = await fetch(`${API_URL}/kategoriler`); const data = await r.json(); setKategoriler(data); if (data.length > 0 && !seciliKategoriId) setSeciliKategoriId(data[0].kategoriID); } catch (e) { console.error(e); } };
  const fetchMasalar = async () => {
    try { const r = await fetch(`${API_URL}/masalar`, { headers }); const data = await r.json(); data.sort((a, b) => (parseInt((a.masaNumarasi || '').replace(/\D/g, ''), 10) || 0) - (parseInt((b.masaNumarasi || '').replace(/\D/g, ''), 10) || 0)); setMasalar(data); }
    catch (e) { console.error(e); }
  };

  const sepeteEkleDirect = useCallback((urun) => { setSepet(prev => { const m = prev.find(i => i.urunID === urun.urunID); return m ? prev.map(i => i.urunID === urun.urunID ? { ...i, adet: i.adet + 1 } : i) : [...prev, { ...urun, adet: 1 }]; }); }, []);
  const sepeteEkle = useCallback((urun) => {
    if (urun.hasModifiers) {
      setModifierUrun(urun);
    } else {
      sepeteEkleDirect(urun);
    }
  }, [sepeteEkleDirect]);
  const sepettenModifierliEkle = useCallback((urun, modifierSecimleri, modifierDetaylari, toplamFiyat) => {
    setSepet(prev => {
      const m = prev.find(i => i.urunID === urun.urunID && JSON.stringify(i.modifierSecimleri) === JSON.stringify(modifierSecimleri));
      return m ? prev.map(i => (i.urunID === urun.urunID && JSON.stringify(i.modifierSecimleri) === JSON.stringify(modifierSecimleri)) ? { ...i, adet: i.adet + 1 } : i) : [...prev, { ...urun, adet: 1, fiyat: toplamFiyat, modifierSecimleri, modifierDetaylari }];
    });
  }, []);
  const adetGuncelle = useCallback((id, adet) => { setSepet(prev => adet <= 0 ? prev.filter(i => i.urunID !== id) : prev.map(i => i.urunID === id ? { ...i, adet } : i)); }, []);
  const notGuncelle = useCallback((id, notMetni) => { setSepet(prev => prev.map(i => i.urunID === id ? { ...i, not: notMetni } : i)); }, []);

  const sepetToplami = useMemo(() => sepet.reduce((t, i) => t + (i.fiyat * i.adet), 0), [sepet]);
  const sepetAdet = useMemo(() => sepet.reduce((t, i) => t + i.adet, 0), [sepet]);
  const normalizedSearch = useMemo(() => normalizeStr(searchTerm), [searchTerm]);
  const searchResults = useMemo(() => {
    if (!normalizedSearch) return null;
    return tumUrunler.filter(u => { if (u.aktif === false) return false; return normalizeStr(u.urunAdi).includes(normalizedSearch) || String(u.urunID) === normalizedSearch; });
  }, [tumUrunler, normalizedSearch]);
  const showingSearch = normalizedSearch && searchResults !== null;
  const urunByKategori = useMemo(() => { const map = {}; tumUrunler.forEach(u => { if (u.aktif !== false) { if (!map[u.kategoriID]) map[u.kategoriID] = []; map[u.kategoriID].push(u); } }); return map; }, [tumUrunler]);
  const gorunenUrunler = useMemo(() => { if (showingSearch) return null; if (!seciliKategoriId) return tumUrunler.filter(u => u.aktif !== false); return urunByKategori[seciliKategoriId] || []; }, [showingSearch, seciliKategoriId, urunByKategori, tumUrunler]);
  const sepetMap = useMemo(() => { const m = {}; sepet.forEach(i => { m[i.urunID] = i.adet; }); return m; }, [sepet]);

  const masaSec = (masa) => {
    if (masa.durum === 'Dolu') {
      fetchMasaAktifSiparis(masa.masaID).then(() => setMasaDetayMasa(masa));
      return;
    }
    if (sepet.length > 0) {
      Swal.fire({ title: 'Sepetiniz boşalacak!', text: 'Masa değiştirirseniz sepetteki ürünler silinecek.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Evet, Değiştir', cancelButtonText: 'Vazgeç', confirmButtonColor: '#10b981' })
        .then(r => { if (r.isConfirmed) { setSeciliMasa(masa); setScreen('siparis'); } });
    } else { setSeciliMasa(masa); setScreen('siparis'); }
  };

  const siparisOlustur = async () => {
    if (!seciliMasa) { Swal.fire('Uyarı', 'Lütfen bir masa seçin!', 'warning'); return; }
    if (sepet.length === 0) { Swal.fire('Uyarı', 'Sepet boş!', 'warning'); return; }
    if (siparisGonderiliyor) return;
    setSiparisGonderiliyor(true);
    try {
      const bodyData = sepet.map(i => ({ urunID: i.urunID, adet: i.adet, notlar: i.not || null, modifierSecimleri: i.modifierDetaylari || i.modifierSecimleri || null }));
      console.log('SIPARIS GONDERILIYOR:', JSON.stringify(bodyData));
      const res = await fetch(`${API_URL}/siparisler`, {
        method: 'POST', headers,
        body: JSON.stringify({ masaId: seciliMasa.masaID, garsonId: kullanici?.kullaniciID, indirimTutari: 0, urunler: bodyData })
      });
      if (res.ok) {
        const respJson = await res.json();
        console.log('SIPARIS CEVAP:', JSON.stringify(respJson));
        const printMsg = respJson.printSummary ? `\n${respJson.printSummary}` : '';
        Swal.fire({ title: 'Başarılı!', text: `Sipariş oluşturuldu!${printMsg}`, icon: 'success', timer: 1500, showConfirmButton: false });
        setSepet([]); fetchMasalar(); fetchMasaAktifSiparis(seciliMasa.masaID);
      } else { const err = await res.json(); console.log('SIPARIS HATA:', JSON.stringify(err)); Swal.fire('Hata!', err.message || err.title || JSON.stringify(err), 'error'); }
    } catch (e) { Swal.fire('Hata', 'Sipariş oluşturulurken hata oluştu!', 'error'); }
    finally { setSiparisGonderiliyor(false); }
  };

  const masaTasi = async (hedefMasaId, birlestir) => {
    if (!mevcutSiparis) return;
    try {
      const res = await fetch(`${API_URL}/siparisler/${mevcutSiparis.siparisID}/masa-tasi`, {
        method: 'POST', headers,
        body: JSON.stringify({ hedefMasaID: hedefMasaId, birlestir })
      });
      if (res.ok) {
        const data = await res.json();
        Swal.fire({ title: 'Taşındı!', text: data.message, icon: 'success', timer: 2000, showConfirmButton: false });
        setMasaDetayMasa(null); setMasaTasiAcik(false);
        setSeciliMasa(null); setMevcutSiparis(null); setScreen('masalar'); fetchMasalar();
      } else { const err = await res.json(); Swal.fire('Hata', err.message || 'Taşıma başarısız', 'error'); }
    } catch (e) { Swal.fire('Hata', 'Taşıma sırasında hata oluştu!', 'error'); }
  };

  const urunTasi = async (detayId, hedefMasaId, adet) => {
    try {
      const res = await fetch(`${API_URL}/siparisler/urun-tasi`, {
        method: 'POST', headers,
        body: JSON.stringify({ detayID: detayId, hedefMasaID: hedefMasaId, adet })
      });
      if (res.ok) {
        const data = await res.json();
        Swal.fire({ title: 'Taşındı!', text: data.message, icon: 'success', timer: 2000, showConfirmButton: false });
        setUrunTasiDetay(null); setMasaDetayMasa(null);
        fetchMasalar(); if (seciliMasa) fetchMasaAktifSiparis(seciliMasa.masaID);
      } else { const err = await res.json(); Swal.fire('Hata', err.message || 'Taşıma başarısız', 'error'); }
    } catch (e) { Swal.fire('Hata', 'Taşıma sırasında hata oluştu!', 'error'); }
  };

  const clearSearch = () => { setSearchTerm(''); searchRef.current?.focus(); };

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const orig = viewport?.getAttribute('content');
    if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    document.body.classList.add('garson-panel-container');
    const preventPinch = (e) => { if (e.touches?.length > 1) e.preventDefault(); };
    document.addEventListener('touchmove', preventPinch, { passive: false });
    return () => {
      if (viewport && orig) viewport.setAttribute('content', orig);
      document.body.classList.remove('garson-panel-container');
      document.removeEventListener('touchmove', preventPinch);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-surface-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-surface-400 font-medium">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (screen === 'masalar') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-surface-900">
        <LockOverlay />
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-surface-800/80 backdrop-blur-xl border-b border-gray-100 dark:border-surface-700/50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              </div>
              <div>
                <h1 className="font-bold text-surface-800 dark:text-white text-sm">Masalar</h1>
                <p className="text-[10px] text-surface-400">{kullanici?.adSoyad || ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchMasalar} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-surface-700 text-gray-400 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </button>
              <button onClick={() => { logout(); navigate('/'); }} className="px-3 py-1.5 text-[11px] font-medium text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">Çıkış</button>
            </div>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {masalar.map(m => (
              <MasaCard key={m.masaID} masa={m} ozet={null} onClick={() => masaSec(m)} />
            ))}
          </div>
        </div>

        {masaDetayMasa && (
          <MasaDetayPopup
            masa={masaDetayMasa}
            mevcutSiparis={mevcutSiparis}
            masalar={masalar}
            onClose={() => { setMasaDetayMasa(null); setMevcutSiparis(null); }}
            onSipariseGit={() => { setSeciliMasa(masaDetayMasa); setScreen('siparis'); }}
            onMasaTasi={(hedefId, birlestir) => masaTasi(hedefId, birlestir)}
            onUrunTasiBaslat={(urun, hedefId, adet) => urunTasi(urun.detayID, hedefId, adet)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="garson-panel-container min-h-screen bg-gray-50 dark:bg-surface-900 flex flex-col">
      <LockOverlay />
      {/* Dark Header */}
      <header className="sticky top-0 z-30 bg-surface-800 dark:bg-surface-900 text-white shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => { setSepet([]); setSeciliMasa(null); setMevcutSiparis(null); setScreen('masalar'); }}
              className="p-1.5 -ml-1.5 rounded-xl hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-sm truncate">{seciliMasa?.masaNumarasi || 'Masa'}</h1>
                <span className="text-[10px] text-white/50">•</span>
                <span className="text-[10px] text-white/60 font-medium truncate">{kullanici?.adSoyad || ''}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {sepetAdet > 0 && <span className="text-[11px] text-emerald-400 font-semibold">{sepetAdet} Ürün • {fm(sepetToplami)}</span>}
                {mevcutSiparis && <span className="text-[10px] text-amber-400">(Mevcut: {fm(mevcutSiparis.toplamTutar)})</span>}
              </div>
            </div>
            <button onClick={() => setKategoriPanelAcik(!kategoriPanelAcik)} className="p-2 rounded-xl hover:bg-white/10 active:bg-white/20 transition-colors touch-manipulation">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
          </div>
          {/* Search */}
          <div className="mt-2.5 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input ref={searchRef} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Ürün adı ile ara"
              className="w-full h-9 pl-9 pr-8 text-sm bg-white/10 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/30 transition-all"
            />
            {searchTerm && (
              <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-white/40 hover:text-white/70 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex max-w-7xl mx-auto w-full">
        {/* Left: Categories (collapsible) */}
        <aside className={`${kategoriPanelAcik ? 'fixed lg:relative inset-0 z-40 lg:z-auto flex' : 'hidden'} flex-col w-64 flex-shrink-0 bg-white dark:bg-surface-800 border-r border-gray-100 dark:border-surface-700`}>
          <div className="p-3 border-b border-gray-100 dark:border-surface-700 flex items-center justify-between">
            <h3 className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Kategoriler</h3>
            <button onClick={() => setKategoriPanelAcik(false)} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-700 text-gray-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-2 custom-scrollbar">
            <div className="px-3 pb-1">
              <button onClick={() => setMasaTasiAcik(true)}
                className="w-full flex items-center gap-2 py-2 px-3 rounded-lg text-[11px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors touch-manipulation mb-2">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                Masa Taşı
              </button>
            </div>
            <button onClick={() => { setSeciliKategoriId(null); setKategoriPanelAcik(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-all touch-manipulation ${!seciliKategoriId ? 'bg-emerald-50 dark:bg-emerald-900/10 border-l-2 border-emerald-500 -ml-px' : 'hover:bg-gray-50 dark:hover:bg-surface-700/30 border-l-2 border-transparent -ml-px'}`}>
              <span className={`flex-1 text-xs font-medium ${!seciliKategoriId ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-surface-600 dark:text-surface-400'}`}>Tümü</span>
            </button>
            <div className="mx-3 my-1.5 border-t border-gray-100 dark:border-surface-700" />
            {kategoriler.map(kat => (
              <KategoriItem key={kat.kategoriID} kat={kat} aktif={seciliKategoriId === kat.kategoriID}
                urunSayisi={(urunByKategori[kat.kategoriID] || []).length}
                onClick={() => { setSeciliKategoriId(kat.kategoriID); setKategoriPanelAcik(false); }} />
            ))}
          </div>
        </aside>
        {/* Overlay for mobile sidebar */}
        {kategoriPanelAcik && (
          <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden" onClick={() => setKategoriPanelAcik(false)} />
        )}

        {/* Right: Products */}
        <div className="flex-1 min-w-0 overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(100vh - 130px)' }}>
          <div className="px-3 py-3">
            {showingSearch ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium text-surface-400">{searchResults.length} sonuç</span>
                  <button onClick={clearSearch} className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium hover:underline touch-manipulation">Temizle</button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {searchResults.map(u => (
                    <UrunCard key={u.urunID} urun={u} adet={sepetMap[u.urunID] || 0} onEkle={sepeteEkle} onAdetGuncelle={adetGuncelle} onLongPress={setLongPressUrun} onNotGuncelle={notGuncelle} />
                  ))}
                </div>
                {searchResults.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-surface-300 dark:text-surface-600">
                    <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <p className="text-xs font-medium">Ürün bulunamadı</p>
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {gorunenUrunler.map(u => (
                  <UrunCard key={u.urunID} urun={u} adet={sepetMap[u.urunID] || 0} onEkle={sepeteEkle} onAdetGuncelle={adetGuncelle} onLongPress={setLongPressUrun} onNotGuncelle={notGuncelle} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="sticky bottom-0 z-30 bg-white dark:bg-surface-800 border-t border-gray-100 dark:border-surface-700 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>
              {sepetAdet > 0 ? (
                <><p className="text-[10px] text-surface-400 font-medium">{sepetAdet} Ürün</p><p className="text-base font-bold text-surface-800 dark:text-white">{fm(sepetToplami)}</p></>
              ) : <p className="text-xs text-surface-400 font-medium">Sepet boş</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {seciliMasa && (
              <button onClick={() => setSiparisGosterAcik(true)}
                className="px-3 py-2.5 text-xs font-semibold bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all active:scale-[0.97] touch-manipulation whitespace-nowrap">
                Siparişleri Göster
              </button>
            )}
            {mevcutSiparis && <span className="text-[10px] text-amber-500 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1 rounded-full font-medium">Mevcut sipariş var</span>}
            <button onClick={siparisOlustur} disabled={sepet.length === 0 || siparisGonderiliyor}
              className={`px-6 py-2.5 text-sm font-bold rounded-xl transition-all active:scale-[0.97] touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed ${
                sepet.length > 0 && !siparisGonderiliyor ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25' : 'bg-gray-100 dark:bg-surface-700 text-gray-400 dark:text-surface-500'
              }`}>
              {siparisGonderiliyor ? 'Gönderiliyor...' : 'Siparişi Al'}
            </button>
          </div>
        </div>
      </div>

      {/* Long press bottom sheet */}
      <UrunDetaySheet urun={longPressUrun} onClose={() => setLongPressUrun(null)} onNotGuncelle={notGuncelle} />

      {/* Modifier bottom sheet */}
      {modifierUrun && (
        <UrunModifierSheet urun={modifierUrun} onClose={() => setModifierUrun(null)} onEkle={sepettenModifierliEkle} />
      )}

      {/* Siparişleri Göster popup */}
      {siparisGosterAcik && (
        <SiparisGosterPopup
          mevcutSiparis={mevcutSiparis}
          sepet={sepet}
          onClose={() => setSiparisGosterAcik(false)}
          onAdetGuncelle={adetGuncelle}
          onNotGuncelle={notGuncelle}
        />
      )}

      {/* Masa Taşı Sheet (from sidebar button) */}
      {masaTasiAcik && seciliMasa && (
        <MasaTasiSheet
          kaynakMasa={seciliMasa}
          masalar={masalar}
          onClose={() => setMasaTasiAcik(false)}
          onTasi={(hedefId, birlestir) => masaTasi(hedefId, birlestir)}
        />
      )}

      {/* Masa Taşı Sheet (when no masa selected - use first dolu masa) */}
      {masaTasiAcik && !seciliMasa && (
        <MasaTasiSheet
          kaynakMasa={masalar.find(m => m.durum === 'Dolu') || masalar[0] || { masaID: 0, masaNumarasi: '?' }}
          masalar={masalar}
          onClose={() => setMasaTasiAcik(false)}
          onTasi={async (hedefId, birlestir) => {
            const kaynak = masalar.find(m => m.durum === 'Dolu');
            if (!kaynak) { Swal.fire('Uyarı', 'Taşınacak aktif sipariş bulunamadı.', 'warning'); return; }
            try {
              const r = await fetch(`${API_URL}/siparisler/masa/${kaynak.masaID}`, { headers });
              const siparisler = await r.json();
              const aktif = siparisler.find(s => s.siparisDurumu !== 'Tamamlandı' && s.siparisDurumu !== 'İptal');
              if (!aktif) { Swal.fire('Uyarı', 'Bu masada aktif sipariş yok.', 'warning'); return; }
              const res = await fetch(`${API_URL}/siparisler/${aktif.siparisID}/masa-tasi`, {
                method: 'POST', headers,
                body: JSON.stringify({ hedefMasaID: hedefId, birlestir })
              });
              if (res.ok) { const data = await res.json(); Swal.fire({ title: 'Taşındı!', text: data.message, icon: 'success', timer: 2000, showConfirmButton: false }); setMasaTasiAcik(false); fetchMasalar(); }
              else { const err = await res.json(); Swal.fire('Hata', err.message || 'Taşıma başarısız', 'error'); }
            } catch (e) { Swal.fire('Hata', 'Taşıma sırasında hata!', 'error'); }
          }}
        />
      )}
    </div>
  );
};

export default GarsonPanel;